import React, { useMemo, useState } from 'react';
import { BarChart3, CalendarRange, Layers, AlertTriangle, Zap, Check, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { analyzePeriod, toLocalDateStr, MAX_SIMULTANEOUS_PER_ACCOUNT } from '../utils/zoomDistribution';

const addDays = (dateStr, n) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return toLocalDateStr(new Date(y, m - 1, d + n));
};
const fmtDay = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-PE', { weekday: 'short', day: '2-digit', month: 'short' });
};
const STATUS = {
    ok: { cls: 'ok', label: 'Alcanza' },
    tight: { cls: 'tight', label: 'Justo' },
    gaps: { cls: 'gaps', label: 'Sin sala' },
    over: { cls: 'over', label: 'Excede' },
};

// Pestaña "Periodo" de Distribuir Cuentas Zoom: analiza un rango con el mismo motor que un día,
// dice cuántas cuentas hacen falta, crea las salas faltantes y distribuye todo el rango de una vez.
const ZoomPeriodPanel = ({ events, accounts, courses, options, creatingRooms, onCreateMissing, onSaveAssignments, onOpenDay }) => {
    const today = toLocalDateStr(new Date());
    const [from, setFrom] = useState(today);
    const [to, setTo] = useState(addDays(today, 30));
    const [ranAt, setRanAt] = useState(null);
    const [includeSaved, setIncludeSaved] = useState(false);
    const [saving, setSaving] = useState(false);

    // Se recalcula solo al pulsar Analizar (o al cambiar eventos/cuentas después de analizar).
    const result = useMemo(() => {
        if (!ranAt || from > to) return null;
        return analyzePeriod({ events, accounts, courses, fromStr: from, toStr: to, options });
    }, [ranAt, from, to, events, accounts, courses, options]);

    // Días ya guardados: por defecto solo se agregan los eventos nuevos (lo guardado queda igual).
    // Con "redistribuir también", el día entero se recalcula. Nunca se tocan "Sin Zoom" ni links manuales.
    const toDistribute = useMemo(() => {
        if (!result) return { days: 0, assignments: [], unassigned: 0 };
        const per = result.days.map(d => (includeSaved ? d.toSaveAll : d.toSave));
        return {
            days: per.filter(list => list.length).length,
            assignments: per.flat(),
            unassigned: result.totals.unassigned,
        };
    }, [result, includeSaved]);

    const t = result?.totals;
    const verdict = !t ? null
        : t.maxAccountsNeeded > t.accountsAvailable
            ? { cls: 'over', text: `Faltan ${t.maxAccountsNeeded - t.accountsAvailable} cuenta(s): el día más cargado necesita ${t.maxAccountsNeeded} y tienes ${t.accountsAvailable}.` }
            : t.maxAccountsNeeded === t.accountsAvailable
                ? { cls: 'tight', text: `Justo: el día más cargado usa las ${t.accountsAvailable} cuentas. Cualquier evento más a esa hora no entra.` }
                : { cls: 'ok', text: `Alcanza: el día más cargado necesita ${t.maxAccountsNeeded} de tus ${t.accountsAvailable} cuentas.` };

    const handleSave = async () => {
        if (!toDistribute.assignments.length) return;
        if (!window.confirm(`Se guardará la distribución de ${toDistribute.days} día(s) (${toDistribute.assignments.length} eventos).${toDistribute.unassigned ? ` ${toDistribute.unassigned} evento(s) quedan sin cuenta y no se tocan.` : ''}${includeSaved ? '\n\nTambién se reemplazan los días que ya tenían distribución guardada.' : ''}\n\n¿Continuar?`)) return;
        setSaving(true);
        try {
            const r = await onSaveAssignments(toDistribute.assignments);
            if (r.failed) toast(`Periodo distribuido: ${r.saved} eventos guardados, ${r.failed} con error`, { icon: '⚠️' });
            else toast.success(`Periodo distribuido: ${r.saved} eventos en ${toDistribute.days} día(s)`);
        } catch (err) {
            console.error(err);
            toast.error('Error al guardar la distribución del periodo');
        } finally { setSaving(false); }
    };

    return (
        <>
            <div className="zd-bar">
                <CalendarRange size={16} className="muted" />
                <label className="muted" htmlFor="zd-from" style={{ fontSize: 13 }}>Desde</label>
                <input id="zd-from" type="date" className="zd-date" value={from} onChange={e => setFrom(e.target.value)} />
                <label className="muted" htmlFor="zd-to" style={{ fontSize: 13 }}>Hasta</label>
                <input id="zd-to" type="date" className="zd-date" value={to} min={from} onChange={e => setTo(e.target.value)} />
                <div className="tabs">
                    {[[7, '7 días'], [30, '30 días'], [60, '60 días']].map(([n, label]) => (
                        <button key={n} type="button" className={from === today && to === addDays(today, n) ? 'on' : ''}
                            onClick={() => { setFrom(today); setTo(addDays(today, n)); }}>{label}</button>
                    ))}
                </div>
                <span className="grow" />
                <button type="button" className="btn pri" onClick={() => setRanAt(Date.now())} disabled={from > to}>
                    <BarChart3 size={16} /> Analizar periodo
                </button>
            </div>

            <div className="zd-body">
                <div className="zd-main">
                    {!result ? (
                        <div className="zd-empty">
                            Elige el rango y pulsa <b>Analizar periodo</b>: verás cuántas cuentas Zoom necesitas cada día,
                            qué horas se pasan de la capacidad y qué cursos no tienen sala.
                        </div>
                    ) : result.days.length === 0 ? (
                        <div className="zd-empty">No hay eventos entre esas fechas.</div>
                    ) : (
                        <>
                            <div className={`zd-verdict ${verdict.cls}`}><Users size={16} /> {verdict.text}</div>
                            <div className="zd-kpis">
                                <div className="card kpi">
                                    <div className="lbl"><span className="ico t-blue"><Layers size={15} /></span>Eventos</div>
                                    <div className="num zd-num">{t.events}</div>
                                    <div className="hint">{t.days} día(s) con clases o MC</div>
                                </div>
                                <div className="card kpi">
                                    <div className="lbl"><span className="ico t-sky"><BarChart3 size={15} /></span>Pico de salas a la vez</div>
                                    <div className="num zd-num">{t.maxPeak}</div>
                                    <div className="hint">máx {MAX_SIMULTANEOUS_PER_ACCOUNT} por cuenta</div>
                                </div>
                                <div className="card kpi">
                                    <div className="lbl"><span className={`ico ${t.maxAccountsNeeded > t.accountsAvailable ? 't-red' : 't-green'}`}><Users size={15} /></span>Cuentas necesarias</div>
                                    <div className="num zd-num">{t.maxAccountsNeeded} <span className="muted" style={{ fontSize: 16 }}>/ {t.accountsAvailable}</span></div>
                                    <div className="hint">en el día más cargado</div>
                                </div>
                                <div className="card kpi">
                                    <div className="lbl"><span className={`ico ${t.overDays || t.unassigned ? 't-amber' : 't-green'}`}><AlertTriangle size={15} /></span>Pendientes</div>
                                    <div className="num zd-num">{t.unassigned}</div>
                                    <div className="hint">evento(s) sin cuenta · {t.overDays} día(s) exceden</div>
                                </div>
                            </div>

                            <div className="card tbl-wrap">
                                <table className="tbl">
                                    <thead>
                                        <tr>
                                            <th>Día</th><th className="r">Eventos</th><th className="r">Pico</th><th className="r">Cuentas</th>
                                            <th>Estado</th><th className="r">Sin cuenta</th><th>Guardado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.days.map(d => (
                                            <tr key={d.date} className="click" onClick={() => onOpenDay(d.date)} title="Abrir este día en la pestaña Día">
                                                <td style={{ fontWeight: 600, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{fmtDay(d.date)}</td>
                                                <td className="r zd-num">{d.events}</td>
                                                <td className="r zd-num">{d.peak}</td>
                                                <td className="r zd-num">{d.accountsNeeded}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <span className={`zd-day-st ${STATUS[d.status].cls}`}>{STATUS[d.status].label}</span>
                                                    {d.reused > 0 && <span className="zd-tag reuse" style={{ marginLeft: 6 }} title="Clases que usan la sala de una MC de las 5 pm">{d.reused} en sala MC</span>}
                                                </td>
                                                <td className="r zd-num" style={{ color: d.unassigned ? 'var(--bad)' : undefined }}>{d.unassigned || '—'}</td>
                                                <td>{d.saved === 'all' ? <span className="pill t-green"><Check size={12} /> Sí</span>
                                                    : d.saved === 'partial' ? <span className="pill t-amber" title="Algunos eventos de este día aún no tienen cuenta">Parcial</span>
                                                    : <span className="muted">No</span>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>

                <aside className="zd-side">
                    {!result ? (
                        <p className="muted" style={{ fontSize: 13 }}>El análisis usa las mismas reglas que la distribución de un día.</p>
                    ) : (
                        <>
                            <div className="zd-section">
                                <h4>Horas críticas</h4>
                                {result.days.every(d => !d.critical.length) ? (
                                    <p className="muted" style={{ fontSize: 13 }}>Ninguna franja supera la capacidad de tus {t.accountsAvailable} cuentas.</p>
                                ) : result.days.flatMap(d => d.critical.map((c, i) => (
                                    <div key={`${d.date}-${i}`} className="zd-crit">
                                        <b style={{ textTransform: 'capitalize' }}>{fmtDay(d.date)}</b> · {c.fromLabel} – {c.toLabel}: {c.count} salas a la vez
                                        <small>{c.titles.join(' · ')}</small>
                                    </div>
                                )))}
                            </div>

                            <div className="zd-section">
                                <h4>Salas faltantes</h4>
                                {t.missingRooms.length === 0 ? (
                                    <p className="muted" style={{ fontSize: 13 }}>Todos los cursos del periodo tienen sala.</p>
                                ) : (
                                    <>
                                        <ul style={{ margin: '0 0 10px', paddingLeft: 16, fontSize: 12.5, display: 'grid', gap: 3 }}>
                                            {t.missingRooms.map(m => <li key={`${m.courseId}-${m.kind}`}>{m.kind === 'masterclass' ? 'MC' : 'Clase'} · {m.courseName}</li>)}
                                        </ul>
                                        <button type="button" className="btn sm" disabled={creatingRooms} onClick={() => onCreateMissing(t.missingRooms)}>
                                            <Zap size={14} /> {creatingRooms ? 'Creando en Zoom…' : `Crear ${t.missingRooms.length} sala(s) en Zoom`}
                                        </button>
                                    </>
                                )}
                            </div>

                            <div className="zd-section">
                                <h4>Distribuir todo el periodo</h4>
                                <label className="zd-check" style={{ marginBottom: 8 }}>
                                    <input type="checkbox" checked={includeSaved} onChange={e => setIncludeSaved(e.target.checked)} />
                                    Redistribuir también los días ya guardados
                                </label>
                                <p className="muted" style={{ fontSize: 12.5, margin: '0 0 10px' }}>
                                    {toDistribute.days} día(s) · {toDistribute.assignments.length} evento(s) por guardar
                                    {toDistribute.unassigned ? ` · ${toDistribute.unassigned} quedan sin cuenta` : ''}
                                    {!includeSaved && result.days.some(d => d.saved) ? ` · lo ya guardado en ${result.days.filter(d => d.saved).length} día(s) se respeta` : ''}
                                    {' · no se tocan los eventos "Sin Zoom" ni los links puestos a mano'}
                                </p>
                                <button type="button" className="btn pri" style={{ width: '100%', justifyContent: 'center' }}
                                    disabled={saving || !toDistribute.assignments.length} onClick={handleSave}>
                                    <Check size={16} /> {saving ? 'Guardando…' : 'Guardar distribución del periodo'}
                                </button>
                            </div>
                        </>
                    )}
                </aside>
            </div>
        </>
    );
};

export default ZoomPeriodPanel;
